import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { typography } from '../../../styles/typography';
import Card from '../../ui/card';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip
);

/**
 * Dish analytics chart: average time spent per chef for a single dish.
 * Mirrors Flutter DishAnalyticsChart: filters by dishName, reads stages (start/end_timestamp)
 * and server_user_name, computes average minutes per chef, shows bar chart.
 *
 * @param {Object} props
 * @param {string} props.dishName - Name of the dish to filter and display.
 * @param {Array<Object>} props.allDishes - Full list of dish dataset rows (each may have stages, server_user_name).
 */
const DishAnalyticsChart = ({ dishName, allDishes = [] }) => {
  const dishRows = useMemo(() => {
    if (!dishName || !Array.isArray(allDishes)) return [];
    const key = dishName.toString().toLowerCase().trim();
    return allDishes.filter((row) => {
      const name = (row?.dish_info?.name ?? '').toString().toLowerCase().trim();
      return name === key;
    });
  }, [dishName, allDishes]);

  // Map: chefName -> list of minutes (from each stage with start/end)
  const chefTimeList = useMemo(() => {
    const map = {};
    for (const dish of dishRows) {
      const stages = dish?.stages;
      if (!stages || typeof stages !== 'object') continue;
      const chefName = dish?.server_user_name;
      if (chefName == null || chefName === '') continue;
      for (const stage of Object.values(stages)) {
        const start = stage?.start_timestamp ?? stage?.start;
        const end = stage?.end_timestamp ?? stage?.end;
        if (start == null || end == null) continue;
        const startTime = new Date(start).getTime();
        const endTime = new Date(end).getTime();
        if (Number.isNaN(startTime) || Number.isNaN(endTime)) continue;
        const minutes = (endTime - startTime) / (60 * 1000);
        if (!map[chefName]) map[chefName] = [];
        map[chefName].push(minutes);
      }
    }
    return map;
  }, [dishRows]);

  // Average time per chef: { chefName: avgMinutes }
  const chefMinutes = useMemo(() => {
    const result = {};
    for (const [chefName, list] of Object.entries(chefTimeList)) {
      if (list.length === 0) continue;
      const sum = list.reduce((a, b) => a + b, 0);
      result[chefName] = sum / list.length;
    }
    return result;
  }, [chefTimeList]);

  const chefNames = useMemo(() => Object.keys(chefMinutes), [chefMinutes]);
  const minutesList = useMemo(
    () => chefNames.map((name) => chefMinutes[name]),
    [chefNames, chefMinutes]
  );
  const maxMinutes = useMemo(() => {
    if (minutesList.length === 0) return 60;
    const max = Math.max(...minutesList);
    return Math.ceil(max) + 15;
  }, [minutesList]);

  const chartData = useMemo(() => {
    if (chefNames.length === 0) return null;
    return {
      labels: chefNames,
      datasets: [
        {
          label: 'Avg time (min)',
          data: minutesList,
          backgroundColor: '#3b82f6',
        },
      ],
    };
  }, [chefNames, minutesList]);

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { bottom: 1, left: 8, right: 8, top: 8 },
      },
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: `Avg time spent for "${dishName || 'Dish'}"`,
          font: { size: 14 },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.parsed.y.toFixed(1)} min`,
          },
        },
      },
      scales: {
        x: {
          position: 'bottom',
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            autoSkip: false,
            font: { size: 11 },
            padding: 1,
          },
        },
        y: {
          beginAtZero: true,
          max: maxMinutes,
          ticks: {
            stepSize: 1,
            callback: (value) => (Number.isInteger(value) ? value : ''),
          },
        },
      },
    }),
    [dishName, maxMinutes]
  );

  const hasChefData = chefNames.length > 0;

  return (
    <div className="w-full space-y-4">
      <div>
        <h2 className={`${typography.h4} text-black mb-1`}>
          Avg time spent for &quot;{dishName || 'Unnamed Dish'}&quot;
        </h2>
        <p className={`${typography.p} text-gray-600 text-sm`}>
          Average minutes per chef from stage start/end timestamps
        </p>
      </div>

      {hasChefData && chartData ? (
        <Card.Gradient className="p-4">
          <div className="overflow-x-auto">
            <div
              style={{
                minWidth: Math.max(300, chefNames.length * 80),
                height: 280,
              }}
            >
              <Bar data={chartData} options={barOptions} />
            </div>
          </div>
        </Card.Gradient>
      ) : (
        <Card.Gradient className="p-6 text-center">
          <p className={typography.p} style={{ color: '#666' }}>
            {dishRows.length === 0
              ? 'No order data for this dish in the current selection.'
              : 'No stage timestamps (start_timestamp / end_timestamp) with server_user_name found for this dish.'}
          </p>
        </Card.Gradient>
      )}
    </div>
  );
};

export default DishAnalyticsChart;
